import type { SudokuState } from "@bing/engine-sudoku";
import {
  finishSudokuSchema,
  SUDOKU_SLOTS,
  withSudokuDefaults,
  type SudokuFinishResponse,
  type SudokuMode,
  type SudokuGame,
  type SudokuSavedState,
  type SudokuSlot,
} from "@bing/shared";
import { ApiError, type ApiClient } from "../../api/client";

/**
 * «سودوکو»'s link to the server for ONE game: the saves, the finish, and what happens when another
 * phone got there first. NEVER A REQUEST PER TAP: a move only remembers the state; it is saved
 * {@link SUDOKU_SAVE_DEBOUNCE_MS} after the LAST move, or at once (with `keepalive`) when the app
 * hides. One save at a time, each on the rev the one before it left.
 *
 * TWO PHONES, ONE GAME: a save names the rev this phone last saw, and the server writes only on
 * that rev. Refused (409), the server's game comes back and this phone ADOPTS it — its own unsaved
 * moves lose, never silently overwrite the other phone's. Coming back to the screen, the newer of the
 * two wins the same way ({@link SudokuSync.adoptIfNewer}).
 *
 * A SOLVED GAME IS NEVER LOST TO THE NETWORK: its finish is kept on the device ({@link KeptFinishes})
 * BEFORE the request leaves, and stays there until the server gives a verdict — retried when the
 * screen is back, as the screen goes, and by the next start screen (the player may have left, or
 * Telegram killed the app). The finish carries the solved grid, so the last moves before it never
 * needed their own save. A LOSS (a "check" game's 3rd mistake) is a finish too — kept, retried and
 * answered the same way — so a lost game is closed on the server even if the network was gone.
 */

export const SUDOKU_SAVE_DEBOUNCE_MS = 3000;

type SyncApi = Pick<ApiClient, "saveSudoku" | "finishSudoku">;

/** What the server answered a finish: done; another phone's game to adopt; refused (nothing to
 *  retry); or no answer (kept, retried when the screen is back). */
export type SudokuFinishOutcome =
  | { kind: "done"; response: SudokuFinishResponse }
  | { kind: "adopt"; game: SudokuGame | null }
  | { kind: "refused" }
  | { kind: "offline" };

function toWire(state: SudokuState): SudokuSavedState {
  return {
    entries: state.entries,
    notes: [...state.notes],
    hinted: [...state.hinted],
    elapsedMs: Math.round(state.elapsedMs),
    undo: state.undo.map(([c, e, n]) => [c, e, n] as [number, number, number]),
    mode: state.mode,
    mistakes: state.mistakes,
    fullShown: state.fullShown,
    revealed: state.revealed,
    marked: [...state.marked],
  };
}

/** The game a 409 carries (`details.game`, with mistake checking's defaults), or null. */
export function conflictGame(error: unknown): SudokuGame | null | undefined {
  if (!(error instanceof ApiError) || error.status !== 409) return undefined;
  const details = error.details as { game?: SudokuGame | null } | undefined;
  return details?.game ? withSudokuDefaults(details.game) : null;
}

/** What a failed finish means: a verdict (409 — the game moved on; 400 — refused) or no answer. */
function finishFailure(error: unknown): Exclude<SudokuFinishOutcome, { kind: "done" }> {
  const theirs = conflictGame(error);
  if (theirs !== undefined) return { kind: "adopt", game: theirs };
  if (error instanceof ApiError && error.status === 400) return { kind: "refused" };
  return { kind: "offline" };
}

/** The slice of `localStorage` a kept finish needs (the tests pass a map). */
export interface SudokuDeviceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** How a game ended, as its finish tells the server: won or lost, its mode, mistakes, hints used and
 *  whether «نشانم بده» was used («assisted»). */
export interface SudokuEnd {
  readonly outcome: "won" | "lost";
  readonly mode: SudokuMode;
  readonly mistakes: number;
  readonly hintsUsed: number;
  readonly assisted: boolean;
}

/** A finished game's finish the server has not answered yet: the slot, the rev it ended on, the grid,
 *  the play time and how it ended — everything the request needs. (One kept before mistake checking
 *  has no end: it reads as a clean "check" win, which it was.) */
export interface KeptFinish extends Partial<SudokuEnd> {
  readonly slot: SudokuSlot;
  readonly rev: number;
  readonly grid: string;
  readonly elapsedMs: number;
}

const KEPT_KEY = "bing:sudokuFinish:";

/**
 * THE FINISHES KEPT ON THE DEVICE — one per slot, keyed by the player (a second Telegram account on
 * the same phone never sends another's). Written BEFORE a finish leaves; dropped on the server's
 * verdict; kept on no answer. Anything unreadable reads as nothing (the server judges what is sent).
 */
export class KeptFinishes {
  constructor(
    private readonly storage: SudokuDeviceStorage,
    /** The player — `profile.id`. */
    private readonly owner: string,
  ) {}

  read(slot: SudokuSlot): KeptFinish | null {
    try {
      const raw = this.storage.getItem(this.key(slot));
      if (raw === null) return null;
      // The request's own schema: what it would refuse is nothing to send.
      const f = finishSudokuSchema.safeParse(JSON.parse(raw));
      if (!f.success || f.data.slot !== slot) return null;
      return f.data;
    } catch {
      return null;
    }
  }

  keep(finish: KeptFinish): void {
    try {
      this.storage.setItem(this.key(finish.slot), JSON.stringify(finish));
    } catch {
      // storage refused (private mode, full): the in-memory finish still retries while on screen
    }
  }

  drop(slot: SudokuSlot): void {
    try {
      this.storage.removeItem(this.key(slot));
    } catch {
      // storage gone: nothing to drop
    }
  }

  /**
   * The start screen, BEFORE it reads the games: every kept finish is sent once. A verdict drops it
   * (done; or a 409 / 400 — the game moved on elsewhere, or the server refuses it); no answer keeps
   * it for the next time. Never rejects.
   */
  async sendAll(api: Pick<ApiClient, "finishSudoku">): Promise<void> {
    for (const slot of SUDOKU_SLOTS) {
      const kept = this.read(slot);
      if (!kept) continue;
      try {
        await api.finishSudoku(kept);
        this.drop(slot);
      } catch (error) {
        if (finishFailure(error).kind !== "offline") this.drop(slot);
      }
    }
  }

  private key(slot: SudokuSlot): string {
    return `${KEPT_KEY}${this.owner}:${slot}`;
  }
}

export class SudokuSync {
  private server: SudokuGame;
  private pending: SudokuState | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;
  /** The finish waiting for the server's verdict (also kept on the device). */
  private pendingFinish: KeptFinish | null = null;
  /** The finish request on the wire — every retry meanwhile waits for it, never sends a second. */
  private finishing: Promise<SudokuFinishOutcome> | null = null;

  constructor(
    private readonly api: SyncApi,
    game: SudokuGame,
    /** Another phone's game replaced this one (null: it is gone — finished or replaced there). */
    private readonly onAdopt: (game: SudokuGame | null) => void,
    /** Where a finish is kept until the server answers it (none: in memory only). */
    private readonly kept: KeptFinishes | null = null,
  ) {
    this.server = game;
  }

  /** The rev this phone last saw from the server. */
  get rev(): number {
    return this.server.rev;
  }

  get hasUnsaved(): boolean {
    return this.pending !== null;
  }

  /** A move: remember the state; the save goes {@link SUDOKU_SAVE_DEBOUNCE_MS} after the last one. */
  changed(state: SudokuState): void {
    this.pending = state;
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush(false);
    }, SUDOKU_SAVE_DEBOUNCE_MS);
  }

  /** Save what is unsaved, now (`keepalive` as the app hides). Nothing unsaved → nothing sent. */
  async flush(keepalive: boolean): Promise<void> {
    this.clearTimer();
    while (this.inFlight) await this.inFlight;
    const state = this.pending;
    if (state === null) return;
    this.pending = null;
    const run = this.save(state, keepalive);
    this.inFlight = run;
    try {
      await run;
    } finally {
      if (this.inFlight === run) this.inFlight = null;
    }
  }

  private async save(state: SudokuState, keepalive: boolean): Promise<void> {
    try {
      const { game } = await this.api.saveSudoku(
        { slot: this.server.slot, rev: this.server.rev, state: toWire(state) },
        { keepalive },
      );
      this.server = game;
    } catch (error) {
      const theirs = conflictGame(error);
      if (theirs !== undefined) {
        this.pending = null;
        if (theirs) this.server = theirs;
        this.onAdopt(theirs);
        return;
      }
      // A 400 is a state the server will never take — retrying it would only repeat the refusal.
      if (error instanceof ApiError && error.status === 400) return;
      // No verdict (the network, a 5xx): keep it for the next flush, unless a newer move came.
      if (this.pending === null) this.pending = state;
    }
  }

  /**
   * The server's game, read as the screen comes back: adopted when it is NEWER than what this phone
   * last saw (another phone played on) — this phone's unsaved moves, on an older rev, are dropped.
   */
  adoptIfNewer(game: SudokuGame | null): boolean {
    if (game === null || game.rev <= this.server.rev) return false;
    this.clearTimer();
    this.pending = null;
    this.server = game;
    this.onAdopt(game);
    return true;
  }

  /**
   * Report the end of the game — the solved grid, or the 3rd mistake — on the rev this phone last
   * saw. It is kept on the device first; with no answer it stays there (and in memory) for
   * {@link retryFinish} and the next start screen.
   */
  async finish(grid: string, elapsedMs: number, end?: SudokuEnd): Promise<SudokuFinishOutcome> {
    this.clearTimer();
    while (this.inFlight) await this.inFlight;
    this.pending = null;
    const finish: KeptFinish = {
      slot: this.server.slot,
      rev: this.server.rev,
      grid,
      elapsedMs: Math.round(elapsedMs),
      ...end,
    };
    this.pendingFinish = finish;
    this.kept?.keep(finish); // before the request: a killed app still has it
    return this.sendFinish();
  }

  /** The kept finish, again (the screen is back, or going); null when none is waiting. */
  async retryFinish(): Promise<SudokuFinishOutcome | null> {
    return this.pendingFinish ? this.sendFinish() : null;
  }

  /**
   * The screen is going: what it still owes the server goes now, with `keepalive` — the finish still
   * waiting for its answer, or else `state` (the moves since the last save, and the time; null for a
   * game already finished). Settles when that is done; never rejects.
   */
  async close(state: SudokuState | null): Promise<void> {
    this.clearTimer();
    if (this.pendingFinish) {
      await this.sendFinish();
      return;
    }
    if (state === null) return;
    this.pending = state;
    await this.flush(true);
  }

  private sendFinish(): Promise<SudokuFinishOutcome> {
    if (this.finishing) return this.finishing;
    const run = this.postFinish().finally(() => {
      this.finishing = null;
    });
    this.finishing = run;
    return run;
  }

  private async postFinish(): Promise<SudokuFinishOutcome> {
    const finish = this.pendingFinish;
    if (!finish) return { kind: "refused" };
    try {
      const response = await this.api.finishSudoku({ ...finish });
      this.settleFinish();
      this.server = response.game;
      return { kind: "done", response };
    } catch (error) {
      const outcome = finishFailure(error);
      if (outcome.kind === "offline") return outcome;
      this.settleFinish();
      if (outcome.kind === "adopt" && outcome.game) this.server = outcome.game;
      return outcome;
    }
  }

  /** The server gave its verdict on the finish: nothing is waiting any more, here or on the device. */
  private settleFinish(): void {
    if (this.pendingFinish) this.kept?.drop(this.pendingFinish.slot);
    this.pendingFinish = null;
  }

  /** Stop the debounce (the screen is going); {@link close} first to keep what is unsaved. */
  dispose(): void {
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
